import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import Lazy from '@prisjakt/ui-lazy';

import { AppContext } from '@prisjakt/ui-app-context';

import { ScrollElement } from 'src/components/scroll';
import Recommendations from 'src/components/recommendations';
import { PageSection } from 'src/components/page';
import { UxLiteQuestions } from 'src/components/ux-feedback';
import { NoStockStatus } from '../../../no-stock-status';
import { TrendingProducts } from '../../../trending-products';
import PriceList from '../../../price-list';
import ReadableProductInformation from '../../../readable-product-information';
import { useProductContext } from '../../../product-context';

import { AppState } from 'src/reducers';

import { ONE_DAY, useShowUXFeedback } from 'src/hooks/use-show-ux-feedback';
import { useLocalStorage } from 'src/hooks/use-localstorage';

import { trackComparisonLinkClick } from 'src/areas/product-compare/tracking';

import { getSimilarProductsCompareLink } from '../../utils';

import { PRODUCT_CONTEXT_ID } from 'src/areas/product/constants';

import { IProductTabMainProps } from 'src/areas/product/components/product-tabs/types';
import { CurrentCampaign } from 'src/areas/product/types';
import { ModelType, ModelTypeEnum } from 'src/components/recommendations/types';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';

import { StyledPageSection } from './styles';

const STATUS_IN_STOCK = 'in_stock';

const ProductTabMain: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IProductTabMainProps>
>> = props => {
  const { product, recommendations, currentCampaign, trendingProducts } = props;
  const priceList = product.prices;
  const [productUXFeedbackTime] = useLocalStorage(
    'pj:productUXFeedbackTime',
    0
  );
  const showUXFeedback = useShowUXFeedback(
    ONE_DAY * 7,
    productUXFeedbackTime,
    0.5
  );

  const { t, linkTo } = useContext(AppContext);
  const { isAnyFilterToggledOnByUser } = useOfferListFilterContext();
  const { offers } = useProductContext();

  const condition = useSelector(
    (state: AppState) => state.areas.product.condition
  );

  const similarProducts = recommendations?.find(
    item => item?.model === ModelTypeEnum.SIMILAR_PRODUCTS
  );
  const handleCompareLinkClick = () => {
    if (!similarProducts?.recommendations?.length) return null;

    return trackComparisonLinkClick(
      similarProducts.recommendations.length + (product ? 1 : 0),
      'similarProducts'
    );
  };

  const similarProductsCompareLink = getSimilarProductsCompareLink(
    similarProducts,
    product,
    linkTo
  );

  //  NoStockStatus prompt should only be shown for 'new' products.
  //  That's because the prompt has an incentive to create a stock alert,
  //  and the stock alert function doesn't send notifications for 'used' products that is out of stock.
  //  Don't show it either if there is any filter activated, because it has it's own empty offer list message.
  const showNoStockStatusMessage =
    product.stockStatus !== STATUS_IN_STOCK &&
    condition === 'new' &&
    !isAnyFilterToggledOnByUser();

  const getRecommendationsLink = (modelType: ModelType) => {
    if (
      modelType === ModelTypeEnum.SIMILAR_PRODUCTS &&
      similarProductsCompareLink
    )
      return {
        title: t('product', 'product-compare.compare'),
        url: similarProductsCompareLink,
        onClick: handleCompareLinkClick,
      };

    if (modelType === ModelTypeEnum.PERSONALIZED)
      return {
        title: t('account', 'menu.settings'),
        url: '/personalization',
      };

    return null;
  };

  return (
    <>
      <StyledPageSection noPad="top" recessed>
        {priceList && (
          <ScrollElement name="Pricelist" data-test="MainTabContent">
            <PriceList
              productId={product.id}
              productName={product.name}
              media={product.media}
              brand={product.brand}
              priceList={priceList}
              conditions={product.conditions}
              campaign={currentCampaign as CurrentCampaign}
            />
          </ScrollElement>
        )}

        {showNoStockStatusMessage && (
          <NoStockStatus
            category={product.category}
            product={product}
            filteredOffers={offers.filteredOffers}
          />
        )}

        <TrendingProducts trendingProducts={trendingProducts} />

        {recommendations?.length ? (
          <Lazy>
            {recommendations.map(item => {
              if (!item.recommendations?.length) return null;
              const model = item.model as ModelType;
              const link = getRecommendationsLink(model);
              return (
                <Recommendations
                  key={`recommendations-model-${model}`}
                  link={link}
                  recommendations={item.recommendations}
                  type={model}
                  contextId={PRODUCT_CONTEXT_ID}
                  background
                />
              );
            })}
          </Lazy>
        ) : null}

        {showUXFeedback && <UxLiteQuestions page="product" />}
      </StyledPageSection>
      <PageSection noBorder>
        <ReadableProductInformation product={product} />
      </PageSection>
    </>
  );
};

export default ProductTabMain;
